import { ONBOARDING_TARGETS, TOTAL_ONBOARDING_STEPS } from '@/constants/onboarding';
import { useAppSelector } from '@/state/hooks';
import { selectCurrentStep, selectIsOnboardingActive } from '@/state/reducers/onboardingSlice';
import { UseLanguageReturn } from '@/utils/hooks/useLanguage';
import useTourManager from '@/utils/hooks/useTourManager';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import OnboardingStep from './steps/OnboardingStep';
import { getOnboardingModuleSteps } from './steps/onboardingModuleSteps';

type StepContentKey = keyof UseLanguageReturn['onboardingSteps'] | string;

interface StepDefinition {
    key: string;
    copyKey: string;
    stepContentKey: StepContentKey;
    targetSelector?: string | string[];
    desktopPosition?: 'top' | 'right' | 'bottom' | 'left' | 'center';
    horizontalPadding?: number;
    canGoBack?: boolean;
    isLastStep?: boolean;
}

const OnboardingContainer: React.FC = () => {
    const { pathname } = useLocation();
    const isActive = useAppSelector(selectIsOnboardingActive);
    const currentStep = useAppSelector(selectCurrentStep);
    const { nextStep, previousStep, skipTour, completeTour } = useTourManager();

    const isTraderRoute = useMemo(() => {
        return pathname.includes('/trader');
    }, [pathname]);

    const handleNext = useCallback(() => {
        if (currentStep === TOTAL_ONBOARDING_STEPS - 1) {
            completeTour();
        } else {
            nextStep();
        }
    }, [currentStep, nextStep, completeTour]);

    const sharedProps = useMemo(
        () => ({
            isActive,
            onNext: handleNext,
            onPrev: previousStep,
            onSkip: skipTour,
            progress: (currentStep / TOTAL_ONBOARDING_STEPS) * 100
        }),
        [isActive, currentStep, handleNext, previousStep, skipTour]
    );

    const steps = useMemo(() => {
        const moduleSteps: StepDefinition[] = getOnboardingModuleSteps(isTraderRoute).map((step) => ({
            key: step.key,
            copyKey: step.copyKey,
            stepContentKey: step.copyKey,
            targetSelector: step.targetSelector,
            desktopPosition: step.desktopPosition,
            horizontalPadding: step.horizontalPadding
        }));

        const baseSteps: StepDefinition[] = [
            {
                key: 'welcome',
                copyKey: 'welcome',
                stepContentKey: 'stepWelcome',
                canGoBack: false
            },
            {
                key: 'bank-wallet',
                copyKey: 'bankWallet',
                stepContentKey: 'stepBankWallet',
                targetSelector: [ONBOARDING_TARGETS.accountBankAccounts, ONBOARDING_TARGETS.accountWallets],
                desktopPosition: 'right'
            },
            {
                key: 'deposit',
                copyKey: 'deposit',
                stepContentKey: 'stepDeposit',
                targetSelector: [ONBOARDING_TARGETS.accountDeposits],
                desktopPosition: 'right'
            },
            {
                key: 'workspace-overview',
                copyKey: 'workspaceOverview',
                stepContentKey: 'workspaceOverview',
                targetSelector: ONBOARDING_TARGETS.sidebar,
                desktopPosition: 'right',
                horizontalPadding: 0
            },
            ...moduleSteps,
            {
                key: 'withdrawal',
                copyKey: 'withdrawal',
                stepContentKey: 'stepWithdrawal',
                targetSelector: [ONBOARDING_TARGETS.accountWithdrawals],
                desktopPosition: 'right'
            },
            {
                key: 'security',
                copyKey: 'security',
                stepContentKey: 'security',
                targetSelector: ONBOARDING_TARGETS.security,
                desktopPosition: 'left'
            },
            {
                key: 'notifications',
                copyKey: 'notifications',
                stepContentKey: 'notifications',
                targetSelector: ONBOARDING_TARGETS.notifications,
                desktopPosition: 'left',
                horizontalPadding: 0
            },
            {
                key: 'other-functions',
                copyKey: 'otherFunctions',
                stepContentKey: 'otherFunctions',
                targetSelector: ONBOARDING_TARGETS.otherFunctions,
                desktopPosition: 'right'
            },
            {
                key: 'help',
                copyKey: 'helpSupport',
                stepContentKey: 'stepHelpSupport',
                isLastStep: true
            }
        ] as const;

        return baseSteps.map((step) => (
            <OnboardingStep
                key={step.key}
                {...sharedProps}
                copyKey={step.copyKey}
                stepContentKey={step.stepContentKey}
                targetSelector={step.targetSelector}
                desktopPosition={step.desktopPosition}
                horizontalPadding={step.horizontalPadding}
                canGoBack={step.canGoBack}
                isLastStep={step.isLastStep}
            />
        ));
    }, [sharedProps, isTraderRoute]);

    return (
        <Fragment>
            {!(!isActive || currentStep >= TOTAL_ONBOARDING_STEPS) && (
                <div className="fixed inset-0 z-[100]">{steps[currentStep]}</div>
            )}
        </Fragment>
    );
};

export default OnboardingContainer;
